import { useCallback, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { calendarWeeklyHeaderToolbar } from '../constants'
import { DateRangePicker } from 'rsuite'
import { IDateCalendar } from '../LocationsEdit'
import { DateRange } from 'rsuite/esm/DateRangePicker'
import 'rsuite/DateRangePicker/styles/index.css'
import { DatesSetArg } from '@fullcalendar/core'
import useCalendar from '../hooks/useCalendar'
import { useGetLocationAvailabilityQuery } from 'services/classes'

export default function WeekCalendar({
  locationId,
  events,
  handleDateChange,
  handleEventClick,
  weeklyView,
  setWeeklyView,
  initialDate,
  currentMonth,
  currentYear
}: {
  locationId: string
  events: any[]
  handleDateChange: any
  handleEventClick?: any
  weeklyView: string
  setWeeklyView: (view: 'order' | 'truck') => void
  initialDate?: IDateCalendar
  currentMonth: number
  currentYear: number
}) {
  const { data: truckPayload } = useGetLocationAvailabilityQuery({
    locationId: locationId,
    month: currentMonth,
    year: currentYear
  })

  const calendarRef = useRef<any>(null)
  const weekPickerEndDay = new Date(initialDate?.end || new Date())
  weekPickerEndDay.setDate(weekPickerEndDay.getDate() - 1)
  const [weekPicker, setWeekPicker] = useState<[Date, Date]>([
    initialDate?.start || new Date(),
    weekPickerEndDay
  ])

  const {
    renderWeeklyEventContent,
    weeklyEventsFiltered,
    renderWeeklyGroupedEvents,
    selectOrderViewButtonDOM,
    selectTruckViewButtonDOM
  } = useCalendar()

  const handleWeekPickerChange = (value: DateRange | null) => {
    if (!value) return
    const [start, end] = value

    setWeekPicker(value)
    handleDateChange({
      start,
      end: end,
      startStr: start.toDateString(),
      endStr: end.toDateString(),
      timeZone: start.getTimezoneOffset().toString(),
      view: 'dayGridWeek' as any
    })

    const calendarApi = calendarRef?.current?.getApi()
    calendarApi?.gotoDate(start, end)
  }

  const handleWeekDateChange = useCallback((dateInfo: DatesSetArg) => {
    const startDate = dateInfo.start
    const endDate = new Date(dateInfo.end)
    endDate.setDate(endDate.getDate() - 1)

    setWeekPicker([startDate, endDate])
    handleDateChange(dateInfo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleHeaderClick = (date: Date) => {
    const clickInfo = {
      event: {
        _instance: {
          range: {
            start: date,
            end: date,
            startStr: date.toDateString(),
            endStr: date.toDateString(),
            time: date.toTimeString(),
            view: 'dayGridDay' as any
          }
        }
      }
    }
    handleEventClick(clickInfo)
  }

  const weeklyCustomButtons = {
    orderView: {
      text: 'Order View',
      click: function () {
        setWeeklyView('order')
        calendarRef?.current.getApi().destroy()
        calendarRef?.current.getApi().render()
        selectOrderViewButtonDOM()
      }
    },
    truckView: {
      text: 'Truck View',
      click: function () {
        setWeeklyView('truck')
        calendarRef?.current.getApi().destroy()
        calendarRef?.current.getApi().render()
        selectTruckViewButtonDOM()
      }
    }
  }

  return (
    <>
      <div className="flex justify-end py-2">
        <DateRangePicker
          value={weekPicker}
          onChange={handleWeekPickerChange}
          format="MM/dd/yyyy"
          character=" – "
          showWeekNumbers
          oneTap
          showOneCalendar
          hoverRange="week"
          ranges={[]}
          cleanable={false}
        />
      </div>
      <FullCalendar
        ref={calendarRef}
        initialDate={initialDate?.start}
        events={events}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridWeek"
        datesSet={handleWeekDateChange}
        headerToolbar={calendarWeeklyHeaderToolbar}
        customButtons={weeklyCustomButtons}
        eventContent={renderWeeklyEventContent}
        eventClick={(arg) => {
          window.location.href = `/admin/orders/${arg.event._def.extendedProps.orderId}`
        }}
        dayHeaderContent={(arg) => {
          if (arg?.view?.type !== 'dayGridWeek') return arg.text

          return (
            <div
              className="cursor-pointer"
              onClick={() => handleHeaderClick(arg.date)}
            >
              {arg.text}
            </div>
          )
        }}
        dayCellDidMount={(arg) => {
          if (weeklyView === 'truck') {
            const dayWrapperElement = arg.el.children[0]
            const dayEventsWrapperElement = arg.el.children[0].children[0]
            setTimeout(() => {
              const dayStart = arg.date
              const dayEnd = new Date(dayStart)
              dayEnd.setDate(dayStart.getDate() + 1)

              const events = calendarRef?.current.getApi().getEvents()
              const filteredEvents = weeklyEventsFiltered(
                events,
                dayStart,
                dayEnd
              )
              renderWeeklyGroupedEvents(
                filteredEvents,
                dayWrapperElement,
                dayEventsWrapperElement,
                truckPayload,
                dayStart
              )
            }, 500)
          }
        }}
      />
    </>
  )
}
